import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Briefcase, MagnifyingGlass, MapPin, Clock, CurrencyDollar } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JobsBoard() {
  const [jobs, setJobs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [experienceFilter, setExperienceFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchJobs = useCallback(async () => {
    try {
      let url = `${API}/jobs?`;
      const params = [];
      if (locationFilter) params.push(`location=${locationFilter}`);
      if (typeFilter) params.push(`job_type=${typeFilter}`);
      if (experienceFilter) params.push(`experience_level=${experienceFilter}`);
      
      const { data } = await axios.get(url + params.join('&'));
      setJobs(data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  }, [locationFilter, typeFilter, experienceFilter]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const filteredJobs = jobs.filter(job =>
    job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background_light">
      <Navbar />
      {/* Hero */}
      <div className="bg-primary text-text_light py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <Briefcase size={48} weight="duotone" />
            <h1 className="text-5xl sm:text-6xl tracking-tight font-semibold">Jobs Board</h1>
          </div>
          <p className="text-xl leading-relaxed text-secondary max-w-4xl">
            Find your next career opportunity in the pest control industry. Browse openings from top employers.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Search & Filters */}
        <div className="bg-white border border-borders rounded-sm p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="relative">
              <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                data-testid="job-search"
                placeholder="Search jobs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <input
              type="text"
              data-testid="location-filter"
              placeholder="Location..."
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="px-4 py-3 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <select
              data-testid="type-filter"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-4 py-3 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Job Types</option>
              <option value="full-time">Full-time</option>
              <option value="part-time">Part-time</option>
              <option value="contract">Contract</option>
            </select>
            <select
              data-testid="experience-filter"
              value={experienceFilter}
              onChange={(e) => setExperienceFilter(e.target.value)}
              className="px-4 py-3 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Experience Levels</option>
              <option value="entry">Entry Level</option>
              <option value="mid">Mid Level</option>
              <option value="senior">Senior Level</option>
            </select>
          </div>
        </div>

        {/* Job Listings */}
        <div>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-medium mb-6">
            Available Positions ({filteredJobs.length})
          </h2>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto"></div>
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="bg-white border border-borders rounded-sm p-8 text-center">
              <p className="text-gray-600">No jobs found. Try adjusting your filters.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {filteredJobs.map(job => (
                <div key={job.id} className="bg-white border border-borders rounded-sm p-6 hover:-translate-y-1 transition-transform" data-testid={`job-${job.id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-2xl tracking-tight font-medium mb-2">{job.title}</h3>
                      {job.company_name && (
                        <p className="text-accent text-sm font-medium mb-2">{job.company_name}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <MapPin size={16} />
                          <span>{job.location}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock size={16} />
                          <span className="capitalize">{job.job_type.replace('-', ' ')}</span>
                        </div>
                        <span className="px-3 py-1 bg-gray-100 text-xs rounded-sm uppercase tracking-[0.2em]">
                          {job.experience_level}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-base leading-relaxed mb-4 text-gray-700">{job.description}</p>
                  
                  {job.requirements && job.requirements.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-medium text-sm uppercase tracking-[0.2em] mb-2">Requirements</h4>
                      <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                        {job.requirements.map((req, idx) => (
                          <li key={req}>{req}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {job.salary_range && (
                    <div className="flex items-center gap-2 mb-4 text-gray-700">
                      <CurrencyDollar size={18} />
                      <span className="text-sm font-medium">{job.salary_range}</span>
                    </div>
                  )}
                  
                  <button
                    data-testid={`apply-job-${job.id}`}
                    onClick={() => toast.info('Application feature coming soon!')}
                    className="bg-primary text-text_light px-6 py-3 rounded-sm hover:bg-primary/90 transition-colors"
                  >
                    Apply Now
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
